import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  DEFAULT_INVOICE_CATALOG,
  calculateManualInvoice,
  createInvoiceDraftBodySchema,
  formatMinorUsd,
  parseUsdToMinor,
  type AdminInvoice,
  type InvoiceDraftLineInput,
  type ProductTypeCode,
} from '@fois-utsavvoice/shared';
import { ApiError } from '../api/client.js';
import {
  createInvoiceDraft,
  deleteInvoiceDraft,
  getInvoice,
  getInvoiceGenerationStatus,
  getInvoicePdfUrl,
  issueInvoice,
  previewInvoicePdf,
  updateInvoiceDraft,
} from '../api/invoices.js';
import { useAuth } from '../auth/AuthContext.js';
import { AdminNav } from '../components/AdminNav.js';
import { InvoicePaymentsPanel } from '../components/InvoicePaymentsPanel.js';
import { InvoiceSharePanel } from '../components/InvoiceSharePanel.js';
import { PageShell } from '../components/PageShell.js';

interface LineDraft {
  description: string;
  quantity: string;
  unitPrice: string;
  discount: string;
  productType?: ProductTypeCode;
}

const emptyLine = (): LineDraft => ({
  description: '',
  quantity: '1',
  unitPrice: '',
  discount: '0',
});

export function InvoiceEditorPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const isNew = !invoiceId || invoiceId === 'new';
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [sourceOrderNumber, setSourceOrderNumber] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [discount, setDiscount] = useState('0');
  const [shipping, setShipping] = useState('0');
  const [tax, setTax] = useState('0');
  const [taxConfirmed, setTaxConfirmed] = useState(false);
  const [terms, setTerms] = useState('Due on receipt');
  const [customerNote, setCustomerNote] = useState('');
  const [includeFeedbackRequest, setIncludeFeedbackRequest] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState<'preview' | 'issue' | 'download' | undefined>();
  const [confirmIssue, setConfirmIssue] = useState(false);
  const [existing, setExisting] = useState<AdminInvoice | undefined>();
  const issued = existing?.status !== undefined && existing.status !== 'DRAFT';
  const pdfReady = existing?.internalGenerationStatus === 'READY';

  useEffect(() => {
    if (isNew || !auth.tokens || !invoiceId) {
      return;
    }
    getInvoice(auth.tokens.idToken, invoiceId)
      .then((invoice) => {
        setExisting(invoice);
        setCustomerName(invoice.customer.name);
        setCustomerEmail(invoice.customer.email ?? '');
        setSourceOrderNumber(invoice.sourceOrderNumber ?? '');
        setLines(
          invoice.lineItems.map((line) => ({
            description: line.description,
            quantity: String(line.quantity),
            unitPrice: centsToInput(line.unitPriceMinor),
            discount: centsToInput(line.discountMinor ?? 0),
            productType: line.productType,
          })),
        );
        setDiscount(centsToInput(invoice.discountMinor));
        setShipping(centsToInput(invoice.shippingMinor));
        setTax(centsToInput(invoice.taxMinor));
        setTaxConfirmed(invoice.taxConfirmed);
        setTerms(invoice.terms ?? 'Due on receipt');
        setCustomerNote(invoice.customerNote ?? '');
        setIncludeFeedbackRequest(invoice.includeFeedbackRequest);
      })
      .catch(() => setError('The invoice could not be loaded.'));
  }, [auth.tokens, invoiceId, isNew]);

  useEffect(() => {
    if (!auth.tokens || !invoiceId || isNew || !issued || pdfReady) {
      return;
    }
    let cancelled = false;
    const timer = window.setInterval(() => {
      getInvoiceGenerationStatus(auth.tokens!.idToken, invoiceId)
        .then((status) => {
          if (cancelled || !status.pdfReady) {
            return;
          }
          setExisting((current) =>
            current
              ? {
                  ...current,
                  invoiceNumber: status.invoiceNumber ?? current.invoiceNumber,
                  internalGenerationStatus: status.internalGenerationStatus,
                  status: status.status,
                }
              : current,
          );
        })
        .catch(() => undefined);
    }, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [auth.tokens, invoiceId, isNew, issued, pdfReady]);

  const preview = useMemo(() => {
    try {
      const lineItems = linesToInputs(lines);
      if (lineItems.length === 0) {
        return undefined;
      }
      return calculateManualInvoice({
        lineItems,
        discountMinor: parseUsdToMinor(discount || '0'),
        shippingMinor: parseUsdToMinor(shipping || '0'),
        taxMinor: parseUsdToMinor(tax || '0'),
        taxConfirmed: parseUsdToMinor(tax || '0') === 0 || taxConfirmed,
      });
    } catch {
      return undefined;
    }
  }, [discount, lines, shipping, tax, taxConfirmed]);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!auth.tokens) {
      return;
    }
    setError(undefined);
    let lineItems: InvoiceDraftLineInput[];
    try {
      lineItems = linesToInputs(lines);
      const parsed = createInvoiceDraftBodySchema.safeParse({
        sourceType: 'MANUAL',
        sourceOrderNumber,
        customer: { name: customerName, email: customerEmail },
        lineItems,
        discountMinor: parseUsdToMinor(discount || '0'),
        shippingMinor: parseUsdToMinor(shipping || '0'),
        taxMinor: parseUsdToMinor(tax || '0'),
        taxConfirmed,
        terms,
        customerNote,
        includeFeedbackRequest,
      });
      if (!parsed.success) {
        setError('Check the customer name, line items, and amounts, then try again.');
        return;
      }
      setSaving(true);
      if (isNew) {
        const created = await createInvoiceDraft(auth.tokens.idToken, parsed.data);
        navigate(`/admin/invoices/${created.invoiceId}`, { replace: true });
        setExisting(created);
      } else if (invoiceId) {
        setExisting(await updateInvoiceDraft(auth.tokens.idToken, invoiceId, parsed.data));
      }
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'The draft could not be saved. Confirm tax if you entered a tax amount.',
      );
    } finally {
      setSaving(false);
    }
  }

  async function openPdf(loader: () => Promise<{ url: string }>): Promise<void> {
    if (!auth.tokens || !invoiceId || isNew) {
      return;
    }
    setError(undefined);
    try {
      const result = await loader();
      window.open(result.url, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'The invoice PDF could not be opened.',
      );
    }
  }

  async function onPreview(): Promise<void> {
    if (!auth.tokens || !invoiceId) {
      return;
    }
    setBusyAction('preview');
    await openPdf(() => previewInvoicePdf(auth.tokens!.idToken, invoiceId));
    setBusyAction(undefined);
  }

  async function onDownload(): Promise<void> {
    if (!auth.tokens || !invoiceId) {
      return;
    }
    setBusyAction('download');
    await openPdf(() => getInvoicePdfUrl(auth.tokens!.idToken, invoiceId));
    setBusyAction(undefined);
  }

  async function onIssue(): Promise<void> {
    if (!auth.tokens || !invoiceId || isNew) {
      return;
    }
    setError(undefined);
    setBusyAction('issue');
    try {
      const storageKey = `invoice-issue:${invoiceId}`;
      const idempotencyKey = sessionStorage.getItem(storageKey) ?? crypto.randomUUID();
      sessionStorage.setItem(storageKey, idempotencyKey);
      const issuedInvoice = await issueInvoice(auth.tokens.idToken, invoiceId, idempotencyKey);
      setExisting(issuedInvoice);
      setConfirmIssue(false);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'The invoice could not be issued.',
      );
    } finally {
      setBusyAction(undefined);
    }
  }

  async function onDelete(): Promise<void> {
    if (!auth.tokens || !invoiceId || isNew) {
      return;
    }
    setSaving(true);
    try {
      await deleteInvoiceDraft(auth.tokens.idToken, invoiceId);
      navigate('/admin/invoices');
    } catch {
      setError('The draft could not be deleted.');
      setSaving(false);
    }
  }

  return (
    <PageShell>
      <AdminNav />
      <section className="card mx-auto max-w-3xl">
        <h1 className="heading-xl text-crimson">
          {isNew ? 'Create manual invoice' : issued ? 'Issued invoice' : 'Invoice draft'}
        </h1>
        <p className="mt-3 text-sm text-charcoal/80">
          Manual invoices are labeled MANUAL. Create the matching Shopify order so inventory and tax
          stay reconciled.
          {issued
            ? ' This invoice is immutable. Download the stored PDF rather than regenerating totals.'
            : ' Drafts display DRAFT until you issue the invoice.'}
        </p>
        {existing ? (
          <p className="mt-2 text-sm font-bold uppercase tracking-brand text-kraft">
            {existing.invoiceNumber ?? 'DRAFT'} · Status {existing.status} ·{' '}
            {formatMinorUsd(existing.totalMinor)}
            {existing.status !== 'DRAFT'
              ? ` · Balance ${formatMinorUsd(existing.balanceDueMinor)}`
              : ''}
            {existing.internalGenerationStatus
              ? ` · PDF ${existing.internalGenerationStatus}`
              : ''}
          </p>
        ) : null}
        {issued && existing?.feedbackGenerationStatus ? (
          <p className="mt-2 text-sm text-charcoal">
            UtsavVoice:{' '}
            {existing.feedbackGenerationStatus === 'READY'
              ? existing.feedbackSubmitted
                ? `submitted${existing.feedbackOverallRating ? ` · ${existing.feedbackOverallRating}/5` : ''}`
                : `request ${existing.feedbackStatus ?? 'ACTIVE'}`
              : existing.feedbackGenerationStatus.toLowerCase()}
            {existing.feedbackRequestId ? (
              <>
                {' '}
                ·{' '}
                <Link
                  className="font-semibold text-crimson underline"
                  to={`/admin/requests/${existing.feedbackRequestId}`}
                >
                  Open feedback request
                </Link>
              </>
            ) : null}
          </p>
        ) : null}

        <form className="mt-6 space-y-6" onSubmit={onSubmit}>
          <fieldset disabled={issued} className="space-y-6">
          <div>
            <label className="field-label" htmlFor="customerName">
              Customer name
            </label>
            <input
              id="customerName"
              required
              className="field-input"
              value={customerName}
              onChange={(event) => setCustomerName(event.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="customerEmail">
              Customer email
            </label>
            <input
              id="customerEmail"
              type="email"
              className="field-input"
              value={customerEmail}
              onChange={(event) => setCustomerEmail(event.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="sourceOrderNumber">
              Shopify or manual order reference
            </label>
            <input
              id="sourceOrderNumber"
              className="field-input"
              value={sourceOrderNumber}
              onChange={(event) => setSourceOrderNumber(event.target.value)}
            />
          </div>

          <fieldset>
            <legend className="field-label">Line items</legend>
            <div className="mb-3 flex flex-wrap gap-2">
              {DEFAULT_INVOICE_CATALOG.map((item) => (
                <button
                  key={item.productType}
                  className="btn-secondary"
                  type="button"
                  onClick={() =>
                    setLines((current) => [
                      ...current,
                      {
                        description: item.displayName,
                        quantity: '1',
                        unitPrice: centsToInput(item.unitPriceMinor),
                        discount: '0',
                        productType: item.productType,
                      },
                    ])
                  }
                >
                  Add {item.displayName}
                </button>
              ))}
            </div>
            <p className="mb-3 text-xs text-charcoal/70">
              Catalog prices are development helpers and are not Shopify inventory.
            </p>
            {lines.map((line, index) => (
              <div key={index} className="mb-3 grid gap-2 sm:grid-cols-4">
                <input
                  aria-label={`Line ${index + 1} description`}
                  className="field-input sm:col-span-2"
                  placeholder="Description"
                  value={line.description}
                  onChange={(event) => updateLine(index, { description: event.target.value })}
                />
                <input
                  aria-label={`Line ${index + 1} quantity`}
                  className="field-input"
                  inputMode="numeric"
                  value={line.quantity}
                  onChange={(event) => updateLine(index, { quantity: event.target.value })}
                />
                <input
                  aria-label={`Line ${index + 1} unit price`}
                  className="field-input"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={line.unitPrice}
                  onChange={(event) => updateLine(index, { unitPrice: event.target.value })}
                />
              </div>
            ))}
            <button
              className="btn-secondary"
              type="button"
              onClick={() => setLines([...lines, emptyLine()])}
            >
              Add line
            </button>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-3">
            <MoneyField
              id="discount"
              label="Order discount"
              value={discount}
              onChange={setDiscount}
            />
            <MoneyField id="shipping" label="Shipping" value={shipping} onChange={setShipping} />
            <MoneyField id="tax" label="Tax amount" value={tax} onChange={setTax} />
          </div>
          <label className="flex min-h-11 items-center gap-3">
            <input
              type="checkbox"
              checked={taxConfirmed}
              onChange={(event) => setTaxConfirmed(event.target.checked)}
            />
            I confirm this tax amount was taken from Shopify or another authoritative source.
            UtsavDesk does not calculate sales tax.
          </label>

          <div>
            <label className="field-label" htmlFor="terms">
              Terms
            </label>
            <input
              id="terms"
              className="field-input"
              value={terms}
              onChange={(event) => setTerms(event.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="customerNote">
              Customer note
            </label>
            <textarea
              id="customerNote"
              className="field-input"
              rows={3}
              value={customerNote}
              onChange={(event) => setCustomerNote(event.target.value)}
            />
          </div>
          <label className="flex min-h-11 items-center gap-3">
            <input
              type="checkbox"
              checked={includeFeedbackRequest}
              onChange={(event) => setIncludeFeedbackRequest(event.target.checked)}
            />
            Include UtsavVoice feedback request when this invoice is later issued
          </label>

          <aside className="border border-linen bg-cream p-4" aria-live="polite">
            <h2 className="heading-lg text-crimson">Totals</h2>
            {preview ? (
              <dl className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt>Subtotal</dt>
                  <dd>{formatMinorUsd(preview.totals.subtotalMinor)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Discount</dt>
                  <dd>{formatMinorUsd(preview.totals.discountMinor)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Shipping</dt>
                  <dd>{formatMinorUsd(preview.totals.shippingMinor)}</dd>
                </div>
                <div className="flex justify-between">
                  <dt>Tax</dt>
                  <dd>{formatMinorUsd(preview.totals.taxMinor)}</dd>
                </div>
                <div className="flex justify-between font-bold">
                  <dt>Total</dt>
                  <dd>{formatMinorUsd(preview.totals.totalMinor)}</dd>
                </div>
              </dl>
            ) : (
              <p className="mt-3 text-sm">Enter valid line items to see the total.</p>
            )}
          </aside>

          </fieldset>
          {error ? (
            <p className="text-sm font-semibold text-crimson" role="alert">
              {error}
            </p>
          ) : null}
          {confirmIssue ? (
            <div className="border border-crimson/30 bg-cream p-4" role="alertdialog">
              <p className="text-sm text-charcoal">
                Issuing assigns a permanent FOIS invoice number and stores an immutable PDF. You
                will not be able to edit this invoice afterward.
              </p>
              <div className="mt-3 flex flex-wrap gap-3">
                <button
                  className="btn-primary"
                  type="button"
                  disabled={busyAction === 'issue'}
                  onClick={() => void onIssue()}
                >
                  {busyAction === 'issue' ? 'Issuing…' : 'Confirm issue'}
                </button>
                <button
                  className="btn-secondary"
                  type="button"
                  onClick={() => setConfirmIssue(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            {!issued ? (
              <button className="btn-primary" type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save draft'}
              </button>
            ) : null}
            {!isNew ? (
              <button
                className="btn-secondary"
                type="button"
                disabled={Boolean(busyAction)}
                onClick={() => void onPreview()}
              >
                {busyAction === 'preview' ? 'Preparing…' : 'Preview'}
              </button>
            ) : null}
            {!isNew && !issued ? (
              <button
                className="btn-primary"
                type="button"
                disabled={saving || Boolean(busyAction)}
                onClick={() => setConfirmIssue(true)}
              >
                Issue invoice
              </button>
            ) : null}
            {issued ? (
              <button
                className="btn-primary"
                type="button"
                disabled={!pdfReady || Boolean(busyAction)}
                onClick={() => void onDownload()}
              >
                {pdfReady
                  ? busyAction === 'download'
                    ? 'Opening…'
                    : 'Download PDF'
                  : 'Generating PDF…'}
              </button>
            ) : null}
            {!isNew && !issued ? (
              <button className="btn-secondary" type="button" disabled={saving} onClick={onDelete}>
                Delete draft
              </button>
            ) : null}
            <Link className="btn-secondary" to="/admin/invoices">
              Back to invoices
            </Link>
          </div>
        </form>
        {issued && existing && auth.tokens ? (
          <InvoiceSharePanel
            invoice={existing}
            accessToken={auth.tokens.idToken}
            onInvoiceChange={setExisting}
            onError={setError}
          />
        ) : null}
        {issued && existing && auth.tokens ? (
          <InvoicePaymentsPanel
            invoice={existing}
            accessToken={auth.tokens.idToken}
            onInvoiceChange={setExisting}
            onError={setError}
          />
        ) : null}
      </section>
    </PageShell>
  );

  function updateLine(index: number, patch: Partial<LineDraft>): void {
    setLines((current) =>
      current.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)),
    );
  }
}

function MoneyField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="field-input"
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function centsToInput(minor: number): string {
  const sign = minor < 0 ? '-' : '';
  const absolute = Math.abs(minor);
  return `${sign}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`;
}

function linesToInputs(lines: LineDraft[]): InvoiceDraftLineInput[] {
  return lines
    .filter((line) => line.description.trim() && line.unitPrice.trim())
    .map((line) => ({
      description: line.description.trim(),
      quantity: Number(line.quantity),
      unitPriceMinor: parseUsdToMinor(line.unitPrice),
      discountMinor: parseUsdToMinor(line.discount || '0'),
      productType: line.productType,
    }));
}
